import { useRef } from 'react';
import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson } from '../../../lib/apiFetchJson';
import {
  AccountsResponseSchema,
  AccountOpportunitiesResponseSchema,
  OpportunityActivityResponseSchema,
  FlowAccountDetailResponseSchema,
  type AccountsResponse,
  type FlowAccountDetailResponse,
} from '@shared/flow/account-view';
import { TimelineResponseSchema } from '@shared/schemas/timeline-entry';
import { AccountHierarchyResponseSchema, type AccountHierarchyResponse } from '@shared/flow/account-hierarchy';
import { AccountHierarchyDealsResponseSchema, type AccountHierarchyDealsResponse } from '@shared/flow/account-hierarchy-deals';
import { AccountWorkspacesResponseSchema, type AccountWorkspacesResponse } from '@shared/flow/account-workspaces';
import { FlowEntitySignalsResponseSchema, type FlowEntitySignalsResponse } from '@shared/flow/entity-signals';
import type { z } from 'zod';
import type { FilterGroup, GroupLogic } from '@shared/flow/filter';
import { FLOW_ACCOUNT_SIGNALS_KEY } from '../contact-state-keys';

/**
 * No `scope` (U5 / R2) — see the contact twin. The account universe is the
 * whole mirror minus unsynced bulk stubs (a server-side provenance guard,
 * Decision 8); everything else is a VISIBLE filter row.
 */
export interface FlowAccountsParams {
  search: string;
  filters: FilterGroup[];
  groupLogic: GroupLogic;
  sort: { field: string; dir: 'ASC' | 'DESC' };
  /** Ordered grouping levels (PR1 U4/D7/D8, R5) — sent as `group_by=a,b,c`. */
  groupBy?: string[];
  /**
   * Gate the fetch (default true). AccountFlowTable holds this false until
   * the role-identity query settles and the role-default filter has been
   * seeded, so the first fetch never fires with unfiltered/pre-seed params
   * (see AccountFlowTable.tsx's seeding effect).
   */
  enabled?: boolean;
}

/** Keyset-paginated accounts list — every param change requeries page 1. */
export function useFlowAccounts(params: FlowAccountsParams) {
  const { apiFetch } = useApi();

  return useInfiniteQuery<AccountsResponse>({
    enabled: params.enabled ?? true,
    queryKey: ['flow-accounts', {
      search: params.search,
      filters: params.filters,
      groupLogic: params.groupLogic,
      sort: params.sort,
      groupBy: params.groupBy ?? [],
    }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        sort: params.sort.field,
        dir: params.sort.dir,
        group_logic: params.groupLogic,
      });
      if (params.search.trim()) qs.set('search', params.search.trim());
      if (params.filters.some(g => g.conditions.length > 0)) qs.set('filters', JSON.stringify(params.filters));
      if (params.groupBy && params.groupBy.length > 0) qs.set('group_by', params.groupBy.join(','));
      if (typeof pageParam === 'string' && pageParam) qs.set('cursor', pageParam);

      return fetchJson(apiFetch, AccountsResponseSchema, `/api/flow/accounts?${qs.toString()}`, { timeoutMs: 30_000 });
    },
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_cursor,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

/**
 * Poll cadence while the detail endpoint reports `'resolving'` (plan D11 —
 * value delegated to implementation, Spencer feels it out in browser QA).
 * 4s is fast enough that a Middleman resolve landing in ~1-2s shows up
 * essentially immediately, and slow enough that the give-up window below is
 * ~30 requests, not hundreds.
 */
export const ACCOUNT_RESOLVE_POLL_MS = 4_000;

/**
 * Bounded give-up window for the `'resolving'` state (plan D11). After this
 * much wall-clock time still resolving, the client stops polling and treats
 * the account as truly not found — the backend's own dedup guard may have
 * gone terminal (a Middleman `NOT_FOUND`, i.e. the id isn't a real SFDC
 * account) without any way to tell the client, so an unbounded spinner would
 * be a permanent dead end. 120s comfortably exceeds a healthy resolve
 * (single Middleman call + one-row insert).
 */
export const ACCOUNT_RESOLVE_GIVE_UP_MS = 120_000;

/**
 * `refetchInterval` for `useFlowAccount`, extracted as a pure function so the
 * polling rule is unit-testable (same shape as
 * `computeRequestsRefetchInterval` in EnrollQueue/requests-hooks.ts).
 *
 * Polling is on ONLY for the account-less `'resolving'` envelope. Every other
 * value is settled: `'synced'`/`'not_synced'` is a mirrored row, `null` is a
 * 404 (or a gave-up resolve, see below), `undefined` is "not fetched yet".
 *
 * `deadlineExceeded` (bug fix, second param): the give-up clock used to be
 * enforced ONLY inside `queryFn`, on a SUCCESSFUL 'resolving' response — but
 * `refetchInterval`'s own callback reads `query.state.data` unconditionally,
 * and React Query does NOT clear `data` on a failed fetch (`retry: false`
 * just means it stops retrying that one attempt, not that the interval
 * timer stops). So once one 'resolving' response landed, `query.state.data`
 * stays `{ state: 'resolving' }` forever through any number of subsequent
 * errors — sustained 500s polled every `ACCOUNT_RESOLVE_POLL_MS` with no
 * give-up, since `queryFn`'s own deadline check never runs (an errored fetch
 * throws before reaching it). The caller now computes `deadlineExceeded`
 * from the SAME `resolveStartedAtRef` `queryFn` maintains, independent of
 * whether the most recent attempt succeeded or failed, and this function
 * honors it even when `data` still says 'resolving'.
 */
export function computeAccountResolveRefetchInterval(
  data: FlowAccountDetailResponse | null | undefined,
  deadlineExceeded = false,
): number | false {
  if (data?.state !== 'resolving') return false;
  return deadlineExceeded ? false : ACCOUNT_RESOLVE_POLL_MS;
}

/** Single account by SFDC id — 404 → null; `'resolving'` polls (plan U4/D11). */
export function useFlowAccount(accountId: string | null) {
  const { apiFetch } = useApi();

  // Wall-clock start of the CURRENT `'resolving'` window, keyed by accountId
  // so navigating between accounts can't inherit the previous one's clock.
  //
  // A ref, but deliberately NOT effect-owned: it is written and read only
  // inside `queryFn`, which never runs during render and has no
  // setup/cleanup lifecycle, so there is no StrictMode double-invoke hazard
  // to reset around (docs/v4-system/strictmode-resilient-effects.md — the
  // rule there is about refs whose lifetime state a mount effect owns). The
  // give-up decision itself stays declarative: it is a function of this
  // timestamp and the response, evaluated where the response arrives.
  const resolveStartedAtRef = useRef<{ accountId: string | null; startedAt: number } | null>(null);

  // Returns the detail ENVELOPE ({ state, account, in_list_universe? }) or
  // null on a genuine 404 (missing/tombstoned row). Any mirrored,
  // non-deleted account resolves — 'not_synced' (old-backend skew) renders
  // identically to 'synced' (plan D3/D4, U2); `in_list_universe === false`
  // means viewable but out of ranked lists, surfaced as a banner, not a 404.
  //
  // `state: 'resolving'` (plan D4/D10) means the id is 001-shaped, had no
  // mirror row, and the backend just fired an on-demand Salesforce resolve:
  // poll until the row lands, then fall through to the normal render. On
  // give-up we return `null` — the SAME value a 404 produces — so both
  // consumers' existing not-found branches cover the gave-up case with no
  // extra flag to thread through (plan U4c: "after give-up, show the
  // existing truly-not-found state").
  return useQuery<FlowAccountDetailResponse | null>({
    queryKey: ['flow-account', accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    retry: false,
    // Bug fix: the deadline must be enforced HERE, independent of whether the
    // most recent fetch attempt succeeded or failed — see
    // `computeAccountResolveRefetchInterval`'s doc-comment. `resolveStartedAtRef`
    // is set inside `queryFn` on the FIRST successful 'resolving' response and
    // is never cleared by a subsequent failed attempt (queryFn throws before
    // reaching any ref mutation on a non-2xx/network error), so it keeps
    // measuring elapsed time across a sustained error run.
    refetchInterval: query => {
      const tracked = resolveStartedAtRef.current;
      const deadlineExceeded =
        !!tracked
        && tracked.accountId === accountId
        && Date.now() - tracked.startedAt >= ACCOUNT_RESOLVE_GIVE_UP_MS;
      return computeAccountResolveRefetchInterval(query.state.data, deadlineExceeded);
    },
    queryFn: async () => {
      let envelope: FlowAccountDetailResponse;
      try {
        envelope = await fetchJson(apiFetch, FlowAccountDetailResponseSchema, `/api/flow/accounts/${accountId}`);
      } catch (err) {
        if (err instanceof ApiProblemError && err.kind === 'http' && err.status === 404) {
          resolveStartedAtRef.current = null;
          return null;
        }
        throw err;
      }

      if (envelope?.state !== 'resolving') {
        resolveStartedAtRef.current = null;
        return envelope;
      }

      const tracked = resolveStartedAtRef.current;
      if (!tracked || tracked.accountId !== accountId) {
        // First 'resolving' response for this account — start the clock.
        resolveStartedAtRef.current = { accountId, startedAt: Date.now() };
        return envelope;
      }
      if (Date.now() - tracked.startedAt >= ACCOUNT_RESOLVE_GIVE_UP_MS) {
        resolveStartedAtRef.current = null;
        return null;
      }
      return envelope;
    },
  });
}

export function useFlowAccountOpportunities(accountId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<z.infer<typeof AccountOpportunitiesResponseSchema>>({
    queryKey: ['flow-account-opps', accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, AccountOpportunitiesResponseSchema, `/api/flow/accounts/${accountId}/opportunities`);
    },
  });
}

/**
 * Lazy per-opp activity (emails + calls) — fetched on expand only (Decision
 * 6). `opportunityId` gates the query the same way `accountId` gates
 * `useFlowAccountOpportunities`: passing `null` (row collapsed / not yet
 * expanded) disables the fetch entirely.
 */
export function useFlowAccountOpportunityActivity(accountId: string | null, opportunityId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<z.infer<typeof OpportunityActivityResponseSchema>>({
    queryKey: ['flow-account-opp-activity', accountId, opportunityId],
    enabled: !!accountId && !!opportunityId,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, OpportunityActivityResponseSchema, `/api/flow/accounts/${accountId}/opportunities/${opportunityId}/activity`);
    },
  });
}

/** The existing core account timeline (server-bucketed entries). */
export function useFlowAccountTimeline(accountId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<z.infer<typeof TimelineResponseSchema>>({
    queryKey: ['flow-account-timeline', accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, TimelineResponseSchema, `/api/accounts/${accountId}/timeline`, { timeoutMs: 30_000 });
    },
  });
}

/**
 * Account action rows (plan §2b item 3, D1's account twin) — the per-account
 * `terra_signals` list (`GET /api/flow/accounts/:id/signals`), merged into
 * `AccountTimeline`'s accordion (U6/R16). `staleTime: Infinity` per the
 * project caching rule; freshness comes from `EnrollQueueContext`'s
 * invalidation of `FLOW_ACCOUNT_SIGNALS_KEY`, which is spread into the key
 * below so the root can never drift between definition and invalidation.
 */
export function useFlowAccountSignals(accountId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<FlowEntitySignalsResponse>({
    queryKey: [...FLOW_ACCOUNT_SIGNALS_KEY, accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      return fetchJson(apiFetch, FlowEntitySignalsResponseSchema, `/api/flow/accounts/${accountId}/signals`, { timeoutMs: 30_000 });
    },
  });
}

/**
 * Bucket E Workspaces accordion (U6) — `account_activity` workspace rows
 * classified into Paid/Trial by the backend (`src/api/account-workspaces.ts`).
 * Fetched unconditionally (not gated on the accordion's own open/collapsed
 * state) so the collapsed trigger can show real "(N paid · M trial)" counts
 * without an expand round-trip — same "always fetch, gate only on accountId"
 * shape as `useFlowAccountTimeline` above.
 */
export function useAccountWorkspaces(accountId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<AccountWorkspacesResponse>({
    queryKey: ['flow-account-workspaces', accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, AccountWorkspacesResponseSchema, `/api/accounts/${accountId}/workspaces`);
    },
  });
}

/**
 * The account's full hierarchy (root + every descendant, capped server-side).
 * Called from the Hierarchy tab's own body, which both shells mount only once
 * the tab is selected — so the request fires on first click, not on page load.
 * `staleTime: Infinity`: nothing mutates a hierarchy from inside Terra, so a
 * reopen reads the cache (a Salesforce re-parent shows on the next reload).
 */
export function useAccountHierarchy(accountId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<AccountHierarchyResponse>({
    queryKey: ['flow-account-hierarchy', accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, AccountHierarchyResponseSchema, `/api/flow/accounts/${accountId}/hierarchy`);
    },
  });
}

/**
 * One account's deals for the Hierarchy overlay's hover tooltip. `enabled` is the tooltip's open
 * state, so nothing is fetched until a rep actually hovers a card. Cached forever like the tree
 * (deal edits land in Salesforce, not here; a reload picks them up).
 */
export function useAccountHierarchyDeals(accountId: string | null, enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery<AccountHierarchyDealsResponse>({
    queryKey: ['flow-account-hierarchy-deals', accountId],
    enabled: !!accountId && enabled,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, AccountHierarchyDealsResponseSchema, `/api/flow/accounts/${accountId}/hierarchy/deals`);
    },
  });
}
